/**
 * constants/layout.ts
 * Layout preset definitions and display maps
 *
 * Provides runtime constants for the layout domain including:
 * - All 20 layout preset configurations with split-tree definitions
 * - Display maps for presets, families, and slots
 * - Default weight values and schema version
 * - Dropdown grouping by preset family
 *
 * These constants correspond to the pure type definitions in `src/types/shared/Layout.ts`.
 *
 * ARCHITECTURAL CONSTRAINTS:
 * - Zero cross-domain imports (only `import type` from `types/`)
 * - No imports from `utils/` or other domain constants
 * - No side effects, pure data only
 * - Immutable maps use `as const satisfies` or explicit `Readonly<Record<>>` annotations
 */

// Internal absolute imports (type-only)
import type { FeatureAreaId } from '@/types/shared/FeatureArea';
import type {
  LayoutPresetId,
  LayoutPresetFamily,
  LayoutPresetConfig,
  SlotId,
  SplitNodeDefinition,
  SplitPointId,
} from '@/types/shared/Layout';

// ===== PRESET IDENTIFIERS =====

/**
 * All layout preset IDs
 * Ordered alphabetically for stable iteration
 */
export const LAYOUT_PRESET_IDS = [
  '2-pane-side-by-side',
  '3-pane-1-left-2-right',
  '3-pane-2-left-1-right',
  '3-pane-three-across',
  '4-pane-grid',
  '4-pane-grid-cols',
  '5-pane-band-bottom',
  '5-pane-band-middle',
  '5-pane-band-top',
  '5-pane-rail-center',
  '5-pane-rail-left',
  '5-pane-rail-right',
  '5-pane-rows-2-3',
  '5-pane-rows-3-2',
  '6-pane-2top-4bottom',
  '6-pane-2x3-columns-first',
  '6-pane-2x3-rows-first',
  '6-pane-3x2-columns-first',
  '6-pane-3x2-rows-first',
  '6-pane-4top-2bottom',
] as const satisfies readonly LayoutPresetId[];

// ===== SLOT IDENTIFIERS =====

/**
 * All slot IDs
 * Ordered numerically for stable iteration
 */
export const SLOT_IDS = [
  '1', '2', '3', '4', '5', '6',
] as const satisfies readonly SlotId[];

// ===== DISPLAY MAPS =====

/**
 * Human-readable labels for preset families (dropdown section headers)
 */
export const LAYOUT_PRESET_FAMILY_DISPLAY_MAP = {
  '2-pane': '2 Pane',
  '3-pane': '3 Pane',
  '4-pane': '4 Pane',
  '5-pane': '5 Pane',
  '6-pane': '6 Pane',
} as const satisfies Readonly<Record<LayoutPresetFamily, string>>;

/**
 * Human-readable labels for slot positions
 */
export const SLOT_DISPLAY_MAP = {
  '1': 'Pane 1',
  '2': 'Pane 2',
  '3': 'Pane 3',
  '4': 'Pane 4',
  '5': 'Pane 5',
  '6': 'Pane 6',
} as const satisfies Readonly<Record<SlotId, string>>;

// ===== PRESET DEFINITIONS =====

// Helper: slot nodes
const slot = (slotId: SlotId): SplitNodeDefinition => ({ type: 'slot', slotId });

// Helper: split nodes
const split = (
  orientation: 'horizontal' | 'vertical',
  splitPointId: SplitPointId,
  first: SplitNodeDefinition,
  second: SplitNodeDefinition,
): SplitNodeDefinition => ({ type: 'split', orientation, splitPointId, first, second });

// ---------------------------------------------------------------------------
// 2-Pane Presets
// ---------------------------------------------------------------------------

/**
 * 2-Pane Side by Side
 *
 * ```
 * H(root)
 * ├── 1
 * └── 2
 * ```
 */
const PRESET_2_PANE_SIDE_BY_SIDE: LayoutPresetConfig = {
  id: '2-pane-side-by-side',
  label: 'Side by Side',
  description: 'Left/right horizontal split — 2 equal panes',
  family: '2-pane',
  slotIds: ['1', '2'],
  splitTree: split('horizontal', 'root',
    slot('1'),
    slot('2'),
  ),
  defaultWeights: { 'root': 0.5 },
  mobileLayout: 'stacked',
};

// ---------------------------------------------------------------------------
// 3-Pane Presets
// ---------------------------------------------------------------------------

/**
 * 3-Pane Three Across
 *
 * ```
 * H(root)
 * ├── 1
 * └── H(right-half)
 *     ├── 2
 *     └── 3
 * ```
 */
const PRESET_3_PANE_THREE_ACROSS: LayoutPresetConfig = {
  id: '3-pane-three-across',
  label: 'Three Across',
  description: '3 equal horizontal panes',
  family: '3-pane',
  slotIds: ['1', '2', '3'],
  splitTree: split('horizontal', 'root',
    slot('1'),
    split('horizontal', 'right-half',
      slot('2'),
      slot('3'),
    ),
  ),
  defaultWeights: { 'root': 0.33, 'right-half': 0.5 },
  mobileLayout: 'stacked',
};

/**
 * 3-Pane 1 Left + 2 Right
 *
 * ```
 * H(root)
 * ├── 1
 * └── V(right-col)
 *     ├── 2
 *     └── 3
 * ```
 */
const PRESET_3_PANE_1_LEFT_2_RIGHT: LayoutPresetConfig = {
  id: '3-pane-1-left-2-right',
  label: '1 Left + 2 Right',
  description: 'One pane left, two stacked right',
  family: '3-pane',
  slotIds: ['1', '2', '3'],
  splitTree: split('horizontal', 'root',
    slot('1'),
    split('vertical', 'right-col',
      slot('2'),
      slot('3'),
    ),
  ),
  defaultWeights: { 'root': 0.33, 'right-col': 0.5 },
  mobileSpanningSlot: '1',
};

/**
 * 3-Pane 2 Left + 1 Right
 * Left branch is the V split; right branch is slot(3).
 *
 * ```
 * H(root)
 * ├── V(left-col)
 * │   ├── 1
 * │   └── 2
 * └── 3
 * ```
 */
const PRESET_3_PANE_2_LEFT_1_RIGHT: LayoutPresetConfig = {
  id: '3-pane-2-left-1-right',
  label: '2 Left + 1 Right',
  description: 'Two stacked left, one pane right',
  family: '3-pane',
  slotIds: ['1', '2', '3'],
  splitTree: split('horizontal', 'root',
    split('vertical', 'left-col',
      slot('1'),
      slot('2'),
    ),
    slot('3'),
  ),
  defaultWeights: { 'root': 0.67, 'left-col': 0.5 },
  mobileSpanningSlot: '3',
};

// ---------------------------------------------------------------------------
// 4-Pane Presets
// ---------------------------------------------------------------------------

/**
 * 4-Pane Grid (2x2, row-primary)
 *
 * ```
 * V(root)
 * ├── H(row-top)
 * │   ├── 1
 * │   └── 2
 * └── H(row-bottom)
 *     ├── 3
 *     └── 4
 * ```
 */
const PRESET_4_PANE_GRID: LayoutPresetConfig = {
  id: '4-pane-grid',
  label: '4 Pane Grid',
  description: '2 columns, 2 rows — 4 equal panes',
  family: '4-pane',
  slotIds: ['1', '2', '3', '4'],
  splitTree: split('vertical', 'root',
    split('horizontal', 'row-top',
      slot('1'),
      slot('2'),
    ),
    split('horizontal', 'row-bottom',
      slot('3'),
      slot('4'),
    ),
  ),
  defaultWeights: { 'root': 0.5, 'row-top': 0.5, 'row-bottom': 0.5 },
};

/**
 * 4-Pane Grid — Columns (2x2, column-primary)
 * Independent vertical dividers per column.
 *
 * ```
 * H(root)
 * ├── V(left-col)
 * │   ├── 1
 * │   └── 3
 * └── V(right-col)
 *     ├── 2
 *     └── 4
 * ```
 */
const PRESET_4_PANE_GRID_COLS: LayoutPresetConfig = {
  id: '4-pane-grid-cols',
  label: '4 Pane Grid — Columns',
  description: '2 columns, 2 rows — independent vertical dividers',
  family: '4-pane',
  slotIds: ['1', '2', '3', '4'],
  splitTree: split('horizontal', 'root',
    split('vertical', 'left-col',
      slot('1'),
      slot('3'),
    ),
    split('vertical', 'right-col',
      slot('2'),
      slot('4'),
    ),
  ),
  defaultWeights: { 'root': 0.5, 'left-col': 0.5, 'right-col': 0.5 },
};

// ---------------------------------------------------------------------------
// 5-Pane Presets — Row-Mixed
// ---------------------------------------------------------------------------

/**
 * 5-Pane Top 2 / Bottom 3
 *
 * ```
 * V(root)
 * ├── H(row-top)
 * │   ├── 1
 * │   └── 2
 * └── H(row-bottom)
 *     ├── 3
 *     └── H(row-bottom-inner)
 *         ├── 4
 *         └── 5
 * ```
 */
const PRESET_5_PANE_ROWS_2_3: LayoutPresetConfig = {
  id: '5-pane-rows-2-3',
  label: 'Top 2 / Bottom 3',
  description: '2 panes top, 3 panes bottom',
  family: '5-pane',
  slotIds: ['1', '2', '3', '4', '5'],
  splitTree: split('vertical', 'root',
    split('horizontal', 'row-top',
      slot('1'),
      slot('2'),
    ),
    split('horizontal', 'row-bottom',
      slot('3'),
      split('horizontal', 'row-bottom-inner',
        slot('4'),
        slot('5'),
      ),
    ),
  ),
  defaultWeights: { 'root': 0.5, 'row-top': 0.5, 'row-bottom': 0.33, 'row-bottom-inner': 0.5 },
  mobileSpanningSlot: '3',
};

/**
 * 5-Pane Top 3 / Bottom 2
 *
 * ```
 * V(root)
 * ├── H(row-top)
 * │   ├── 1
 * │   └── H(row-top-inner)
 * │       ├── 2
 * │       └── 3
 * └── H(row-bottom)
 *     ├── 4
 *     └── 5
 * ```
 */
const PRESET_5_PANE_ROWS_3_2: LayoutPresetConfig = {
  id: '5-pane-rows-3-2',
  label: 'Top 3 / Bottom 2',
  description: '3 panes top, 2 panes bottom',
  family: '5-pane',
  slotIds: ['1', '2', '3', '4', '5'],
  splitTree: split('vertical', 'root',
    split('horizontal', 'row-top',
      slot('1'),
      split('horizontal', 'row-top-inner',
        slot('2'),
        slot('3'),
      ),
    ),
    split('horizontal', 'row-bottom',
      slot('4'),
      slot('5'),
    ),
  ),
  defaultWeights: { 'root': 0.5, 'row-top': 0.33, 'row-top-inner': 0.5, 'row-bottom': 0.5 },
  mobileSpanningSlot: '3',
};

// ---------------------------------------------------------------------------
// 5-Pane Presets — Bands (full-width row)
// ---------------------------------------------------------------------------

/**
 * 5-Pane Top Full Band
 *
 * ```
 * V(root)
 * ├── 1 (full-width band)
 * └── V(sub-rows)
 *     ├── H(row-top)
 *     │   ├── 2
 *     │   └── 3
 *     └── H(row-bottom)
 *         ├── 4
 *         └── 5
 * ```
 */
const PRESET_5_PANE_BAND_TOP: LayoutPresetConfig = {
  id: '5-pane-band-top',
  label: 'Top Full Band',
  description: 'Full-width top band, 2x2 grid below',
  family: '5-pane',
  slotIds: ['1', '2', '3', '4', '5'],
  splitTree: split('vertical', 'root',
    slot('1'),
    split('vertical', 'sub-rows',
      split('horizontal', 'row-top',
        slot('2'),
        slot('3'),
      ),
      split('horizontal', 'row-bottom',
        slot('4'),
        slot('5'),
      ),
    ),
  ),
  defaultWeights: { 'root': 0.33, 'sub-rows': 0.5, 'row-top': 0.5, 'row-bottom': 0.5 },
  mobileSpanningSlot: '1',
};

/**
 * 5-Pane Middle Full Band
 *
 * ```
 * V(root)
 * ├── H(row-top)
 * │   ├── 1
 * │   └── 2
 * └── V(sub-rows)
 *     ├── 3 (full-width band)
 *     └── H(row-bottom)
 *         ├── 4
 *         └── 5
 * ```
 */
const PRESET_5_PANE_BAND_MIDDLE: LayoutPresetConfig = {
  id: '5-pane-band-middle',
  label: 'Middle Full Band',
  description: '2 panes top, full-width middle band, 2 panes bottom',
  family: '5-pane',
  slotIds: ['1', '2', '3', '4', '5'],
  splitTree: split('vertical', 'root',
    split('horizontal', 'row-top',
      slot('1'),
      slot('2'),
    ),
    split('vertical', 'sub-rows',
      slot('3'),
      split('horizontal', 'row-bottom',
        slot('4'),
        slot('5'),
      ),
    ),
  ),
  defaultWeights: { 'root': 0.33, 'row-top': 0.5, 'sub-rows': 0.5, 'row-bottom': 0.5 },
  mobileSpanningSlot: '3',
};

/**
 * 5-Pane Bottom Full Band
 *
 * ```
 * V(root)
 * ├── H(row-top)
 * │   ├── 1
 * │   └── 2
 * └── V(sub-rows)
 *     ├── H(row-bottom)
 *     │   ├── 3
 *     │   └── 4
 *     └── 5 (full-width band)
 * ```
 */
const PRESET_5_PANE_BAND_BOTTOM: LayoutPresetConfig = {
  id: '5-pane-band-bottom',
  label: 'Bottom Full Band',
  description: '2x2 grid top, full-width bottom band',
  family: '5-pane',
  slotIds: ['1', '2', '3', '4', '5'],
  splitTree: split('vertical', 'root',
    split('horizontal', 'row-top',
      slot('1'),
      slot('2'),
    ),
    split('vertical', 'sub-rows',
      split('horizontal', 'row-bottom',
        slot('3'),
        slot('4'),
      ),
      slot('5'),
    ),
  ),
  defaultWeights: { 'root': 0.33, 'row-top': 0.5, 'sub-rows': 0.5, 'row-bottom': 0.5 },
  mobileSpanningSlot: '5',
};

// ---------------------------------------------------------------------------
// 5-Pane Presets — Rails (full-height column)
// ---------------------------------------------------------------------------

/**
 * 5-Pane Left Full Rail
 *
 * ```
 * H(root)
 * ├── 1 (full-height rail)
 * └── V(right-col)
 *     ├── H(row-top)
 *     │   ├── 2
 *     │   └── 3
 *     └── H(row-bottom)
 *         ├── 4
 *         └── 5
 * ```
 */
const PRESET_5_PANE_RAIL_LEFT: LayoutPresetConfig = {
  id: '5-pane-rail-left',
  label: 'Left Full Rail',
  description: 'Full-height left rail, 2x2 grid on right',
  family: '5-pane',
  slotIds: ['1', '2', '3', '4', '5'],
  splitTree: split('horizontal', 'root',
    slot('1'),
    split('vertical', 'right-col',
      split('horizontal', 'row-top',
        slot('2'),
        slot('3'),
      ),
      split('horizontal', 'row-bottom',
        slot('4'),
        slot('5'),
      ),
    ),
  ),
  defaultWeights: { 'root': 0.33, 'right-col': 0.5, 'row-top': 0.5, 'row-bottom': 0.5 },
  mobileSpanningSlot: '1',
};

/**
 * 5-Pane Center Full Rail
 *
 * ```
 * H(root)
 * ├── V(left-col)
 * │   ├── 1
 * │   └── 4
 * └── H(right-half)
 *     ├── 2 (full-height rail)
 *     └── V(right-col)
 *         ├── 3
 *         └── 5
 * ```
 */
const PRESET_5_PANE_RAIL_CENTER: LayoutPresetConfig = {
  id: '5-pane-rail-center',
  label: 'Center Full Rail',
  description: 'Left column, full-height center rail, right column',
  family: '5-pane',
  slotIds: ['1', '2', '3', '4', '5'],
  splitTree: split('horizontal', 'root',
    split('vertical', 'left-col',
      slot('1'),
      slot('4'),
    ),
    split('horizontal', 'right-half',
      slot('2'),
      split('vertical', 'right-col',
        slot('3'),
        slot('5'),
      ),
    ),
  ),
  defaultWeights: { 'root': 0.33, 'left-col': 0.5, 'right-half': 0.5, 'right-col': 0.5 },
  mobileSpanningSlot: '3',
};

/**
 * 5-Pane Right Full Rail
 *
 * ```
 * H(root)
 * ├── V(left-col)
 * │   ├── H(row-top)
 * │   │   ├── 1
 * │   │   └── 2
 * │   └── H(row-bottom)
 * │       ├── 4
 * │       └── 5
 * └── 3 (full-height rail)
 * ```
 */
const PRESET_5_PANE_RAIL_RIGHT: LayoutPresetConfig = {
  id: '5-pane-rail-right',
  label: 'Right Full Rail',
  description: '2x2 grid on left, full-height right rail',
  family: '5-pane',
  slotIds: ['1', '2', '3', '4', '5'],
  splitTree: split('horizontal', 'root',
    split('vertical', 'left-col',
      split('horizontal', 'row-top',
        slot('1'),
        slot('2'),
      ),
      split('horizontal', 'row-bottom',
        slot('4'),
        slot('5'),
      ),
    ),
    slot('3'),
  ),
  defaultWeights: { 'root': 0.67, 'left-col': 0.5, 'row-top': 0.5, 'row-bottom': 0.5 },
  mobileSpanningSlot: '5',
};

// ---------------------------------------------------------------------------
// 6-Pane Presets
// ---------------------------------------------------------------------------

/**
 * 6-Pane 3×2 Grid — Rows (rows-first)
 * Shared horizontal divider; vertical dividers independent per row.
 *
 * ```
 * V(root)
 * ├── H(row-top)
 * │   ├── 1
 * │   └── H(row-top-inner)
 * │       ├── 2
 * │       └── 3
 * └── H(row-bottom)
 *     ├── 4
 *     └── H(row-bottom-inner)
 *         ├── 5
 *         └── 6
 * ```
 */
const PRESET_6_PANE_3X2_ROWS_FIRST: LayoutPresetConfig = {
  id: '6-pane-3x2-rows-first',
  label: '3×2 Grid — Rows',
  description: '3 columns, 2 rows — shared horizontal divider',
  family: '6-pane',
  slotIds: ['1', '2', '3', '4', '5', '6'],
  splitTree: split('vertical', 'root',
    split('horizontal', 'row-top',
      slot('1'),
      split('horizontal', 'row-top-inner',
        slot('2'),
        slot('3'),
      ),
    ),
    split('horizontal', 'row-bottom',
      slot('4'),
      split('horizontal', 'row-bottom-inner',
        slot('5'),
        slot('6'),
      ),
    ),
  ),
  defaultWeights: {
    'root': 0.5,
    'row-top': 0.33,
    'row-top-inner': 0.5,
    'row-bottom': 0.33,
    'row-bottom-inner': 0.5,
  },
};

/**
 * 6-Pane 3×2 Grid — Columns (columns-first)
 * Shared vertical dividers; horizontal dividers independent per column.
 *
 * ```
 * H(root)
 * ├── V(left-col)
 * │   ├── 1
 * │   └── 4
 * └── H(right-half)
 *     ├── V(mid-col)
 *     │   ├── 2
 *     │   └── 5
 *     └── V(right-col)
 *         ├── 3
 *         └── 6
 * ```
 */
const PRESET_6_PANE_3X2_COLUMNS_FIRST: LayoutPresetConfig = {
  id: '6-pane-3x2-columns-first',
  label: '3×2 Grid — Columns',
  description: '3 columns, 2 rows — shared vertical dividers',
  family: '6-pane',
  slotIds: ['1', '2', '3', '4', '5', '6'],
  splitTree: split('horizontal', 'root',
    split('vertical', 'left-col',
      slot('1'),
      slot('4'),
    ),
    split('horizontal', 'right-half',
      split('vertical', 'mid-col',
        slot('2'),
        slot('5'),
      ),
      split('vertical', 'right-col',
        slot('3'),
        slot('6'),
      ),
    ),
  ),
  defaultWeights: {
    'root': 0.33,
    'left-col': 0.5,
    'right-half': 0.5,
    'mid-col': 0.5,
    'right-col': 0.5,
  },
};

/**
 * 6-Pane 2×3 Grid — Rows (rows-first)
 * Shared horizontal dividers; vertical divider independent per row.
 *
 * ```
 * V(root)
 * ├── H(row-top)
 * │   ├── 1
 * │   └── 2
 * └── V(sub-rows)
 *     ├── H(row-middle)
 *     │   ├── 3
 *     │   └── 4
 *     └── H(row-bottom)
 *         ├── 5
 *         └── 6
 * ```
 */
const PRESET_6_PANE_2X3_ROWS_FIRST: LayoutPresetConfig = {
  id: '6-pane-2x3-rows-first',
  label: '2×3 Grid — Rows',
  description: '2 columns, 3 rows — shared horizontal dividers',
  family: '6-pane',
  slotIds: ['1', '2', '3', '4', '5', '6'],
  splitTree: split('vertical', 'root',
    split('horizontal', 'row-top',
      slot('1'),
      slot('2'),
    ),
    split('vertical', 'sub-rows',
      split('horizontal', 'row-middle',
        slot('3'),
        slot('4'),
      ),
      split('horizontal', 'row-bottom',
        slot('5'),
        slot('6'),
      ),
    ),
  ),
  defaultWeights: {
    'root': 0.33,
    'row-top': 0.5,
    'sub-rows': 0.5,
    'row-middle': 0.5,
    'row-bottom': 0.5,
  },
};

/**
 * 6-Pane 2×3 Grid — Columns (columns-first)
 * Shared vertical divider; horizontal dividers independent per column.
 *
 * ```
 * H(root)
 * ├── V(left-col)
 * │   ├── 1
 * │   └── V(left-col-inner)
 * │       ├── 3
 * │       └── 5
 * └── V(right-col)
 *     ├── 2
 *     └── V(right-col-inner)
 *         ├── 4
 *         └── 6
 * ```
 */
const PRESET_6_PANE_2X3_COLUMNS_FIRST: LayoutPresetConfig = {
  id: '6-pane-2x3-columns-first',
  label: '2×3 Grid — Columns',
  description: '2 columns, 3 rows — shared vertical divider',
  family: '6-pane',
  slotIds: ['1', '2', '3', '4', '5', '6'],
  splitTree: split('horizontal', 'root',
    split('vertical', 'left-col',
      slot('1'),
      split('vertical', 'left-col-inner',
        slot('3'),
        slot('5'),
      ),
    ),
    split('vertical', 'right-col',
      slot('2'),
      split('vertical', 'right-col-inner',
        slot('4'),
        slot('6'),
      ),
    ),
  ),
  defaultWeights: {
    'root': 0.5,
    'left-col': 0.33,
    'left-col-inner': 0.5,
    'right-col': 0.33,
    'right-col-inner': 0.5,
  },
};

/**
 * 6-Pane 2 Top / 4 Bottom
 * 2 panes top row, balanced 4-pane bottom row.
 * Shared horizontal divider; 60% height to big top row.
 *
 * ```
 * V(root)
 * ├── H(row-top)
 * │   ├── 1
 * │   └── 2
 * └── H(row-bottom)
 *     ├── H(row-bottom-left)
 *     │   ├── 3
 *     │   └── 4
 *     └── H(row-bottom-right)
 *         ├── 5
 *         └── 6
 * ```
 */
const PRESET_6_PANE_2TOP_4BOTTOM: LayoutPresetConfig = {
  id: '6-pane-2top-4bottom',
  label: '2 Top / 4 Bottom',
  description: '2 panes top, 4 panes bottom',
  family: '6-pane',
  slotIds: ['1', '2', '3', '4', '5', '6'],
  splitTree: split('vertical', 'root',
    split('horizontal', 'row-top',
      slot('1'),
      slot('2'),
    ),
    split('horizontal', 'row-bottom',
      split('horizontal', 'row-bottom-left',
        slot('3'),
        slot('4'),
      ),
      split('horizontal', 'row-bottom-right',
        slot('5'),
        slot('6'),
      ),
    ),
  ),
  defaultWeights: {
    'root': 0.6,
    'row-top': 0.5,
    'row-bottom': 0.5,
    'row-bottom-left': 0.5,
    'row-bottom-right': 0.5,
  },
};

/**
 * 6-Pane 4 Top / 2 Bottom
 * Balanced 4-pane top row, 2 panes bottom row.
 * Shared horizontal divider; 40% height to small top row.
 *
 * ```
 * V(root)
 * ├── H(row-top)
 * │   ├── H(row-top-left)
 * │   │   ├── 1
 * │   │   └── 2
 * │   └── H(row-top-right)
 * │       ├── 3
 * │       └── 4
 * └── H(row-bottom)
 *     ├── 5
 *     └── 6
 * ```
 */
const PRESET_6_PANE_4TOP_2BOTTOM: LayoutPresetConfig = {
  id: '6-pane-4top-2bottom',
  label: '4 Top / 2 Bottom',
  description: '4 panes top, 2 panes bottom',
  family: '6-pane',
  slotIds: ['1', '2', '3', '4', '5', '6'],
  splitTree: split('vertical', 'root',
    split('horizontal', 'row-top',
      split('horizontal', 'row-top-left',
        slot('1'),
        slot('2'),
      ),
      split('horizontal', 'row-top-right',
        slot('3'),
        slot('4'),
      ),
    ),
    split('horizontal', 'row-bottom',
      slot('5'),
      slot('6'),
    ),
  ),
  defaultWeights: {
    'root': 0.4,
    'row-top': 0.5,
    'row-top-left': 0.5,
    'row-top-right': 0.5,
    'row-bottom': 0.5,
  },
};

// ===== PRESET COLLECTION =====

/**
 * All layout presets keyed by ID
 */
export const LAYOUT_PRESETS: Readonly<Record<LayoutPresetId, LayoutPresetConfig>> = {
  '2-pane-side-by-side': PRESET_2_PANE_SIDE_BY_SIDE,
  '3-pane-1-left-2-right': PRESET_3_PANE_1_LEFT_2_RIGHT,
  '3-pane-2-left-1-right': PRESET_3_PANE_2_LEFT_1_RIGHT,
  '3-pane-three-across': PRESET_3_PANE_THREE_ACROSS,
  '4-pane-grid': PRESET_4_PANE_GRID,
  '4-pane-grid-cols': PRESET_4_PANE_GRID_COLS,
  '5-pane-band-bottom': PRESET_5_PANE_BAND_BOTTOM,
  '5-pane-band-middle': PRESET_5_PANE_BAND_MIDDLE,
  '5-pane-band-top': PRESET_5_PANE_BAND_TOP,
  '5-pane-rail-center': PRESET_5_PANE_RAIL_CENTER,
  '5-pane-rail-left': PRESET_5_PANE_RAIL_LEFT,
  '5-pane-rail-right': PRESET_5_PANE_RAIL_RIGHT,
  '5-pane-rows-2-3': PRESET_5_PANE_ROWS_2_3,
  '5-pane-rows-3-2': PRESET_5_PANE_ROWS_3_2,
  '6-pane-2top-4bottom': PRESET_6_PANE_2TOP_4BOTTOM,
  '6-pane-2x3-columns-first': PRESET_6_PANE_2X3_COLUMNS_FIRST,
  '6-pane-2x3-rows-first': PRESET_6_PANE_2X3_ROWS_FIRST,
  '6-pane-3x2-columns-first': PRESET_6_PANE_3X2_COLUMNS_FIRST,
  '6-pane-3x2-rows-first': PRESET_6_PANE_3X2_ROWS_FIRST,
  '6-pane-4top-2bottom': PRESET_6_PANE_4TOP_2BOTTOM,
};

// ===== DERIVED DISPLAY MAPS =====

/**
 * Human-readable labels for layout presets.
 * Derived from LAYOUT_PRESETS via LAYOUT_PRESET_IDS for single source of truth.
 */
export const LAYOUT_PRESET_DISPLAY_MAP: Readonly<Record<LayoutPresetId, string>> =
  Object.fromEntries(
    LAYOUT_PRESET_IDS.map((id) => [id, LAYOUT_PRESETS[id].label])
  ) as Record<LayoutPresetId, string>;

/**
 * Preset family for each preset ID.
 * Derived from LAYOUT_PRESETS via LAYOUT_PRESET_IDS for single source of truth.
 */
export const LAYOUT_PRESET_FAMILY_MAP: Readonly<Record<LayoutPresetId, LayoutPresetFamily>> =
  Object.fromEntries(
    LAYOUT_PRESET_IDS.map((id) => [id, LAYOUT_PRESETS[id].family])
  ) as Record<LayoutPresetId, LayoutPresetFamily>;

// ===== FAMILY GROUPING =====

/**
 * Preset IDs grouped by family for dropdown section rendering.
 * Order within each family is intentional (row-mixed, bands, rails).
 */
export const LAYOUT_PRESETS_BY_FAMILY: Readonly<Record<LayoutPresetFamily, readonly LayoutPresetId[]>> = {
  '2-pane': ['2-pane-side-by-side'],
  '3-pane': ['3-pane-three-across', '3-pane-1-left-2-right', '3-pane-2-left-1-right'],
  '4-pane': ['4-pane-grid', '4-pane-grid-cols'],
  '5-pane': [
    '5-pane-rows-2-3',
    '5-pane-rows-3-2',
    '5-pane-band-top',
    '5-pane-band-middle',
    '5-pane-band-bottom',
    '5-pane-rail-left',
    '5-pane-rail-center',
    '5-pane-rail-right',
  ],
  '6-pane': [
    '6-pane-3x2-rows-first',
    '6-pane-3x2-columns-first',
    '6-pane-2x3-rows-first',
    '6-pane-2x3-columns-first',
    '6-pane-2top-4bottom',
    '6-pane-4top-2bottom',
  ],
};

// ===== DEFAULTS =====

/** Default preset ID for new layout configurations */
export const DEFAULT_PRESET_ID: LayoutPresetId = '6-pane-3x2-rows-first';

/** Layout config schema version (bumped per structural change) */
export const DEFAULT_LAYOUT_SCHEMA_VERSION = 3;

/** Default slot-to-feature mapping — all 6 features assigned to slots 1–6 */
export const DEFAULT_SLOT_ASSIGNMENTS = {
  '1': 'trades',
  '2': 'notes',
  '3': 'market-state',
  '4': 'economic-events',
  '5': 'objectives',
  '6': 'break-reset',
} as const satisfies Record<SlotId, FeatureAreaId>;

/** Presets available in the current phase — all 20 presets enabled */
export const PHASE_AVAILABLE_PRESETS: readonly LayoutPresetId[] = [
  '2-pane-side-by-side',
  '3-pane-three-across',
  '3-pane-1-left-2-right',
  '3-pane-2-left-1-right',
  '4-pane-grid',
  '4-pane-grid-cols',
  '5-pane-rows-2-3',
  '5-pane-rows-3-2',
  '5-pane-band-top',
  '5-pane-band-middle',
  '5-pane-band-bottom',
  '5-pane-rail-left',
  '5-pane-rail-center',
  '5-pane-rail-right',
  '6-pane-3x2-rows-first',
  '6-pane-3x2-columns-first',
  '6-pane-2x3-rows-first',
  '6-pane-2x3-columns-first',
  '6-pane-2top-4bottom',
  '6-pane-4top-2bottom',
];

/** Fallback when stored preset is not in PHASE_AVAILABLE_PRESETS */
export const FALLBACK_PRESET_ID: LayoutPresetId = '4-pane-grid';

/**
 * Visually distinct presets on mobile (9 total).
 *
 * Mobile renders presets as a CSS grid where desktop split-tree structure
 * (row-first vs column-first, nesting depth) has no visual effect.
 * Differentiators are `mobileLayout` ('stacked' vs 'grid') and
 * `mobileSpanningSlot` (which slot gets `col-span-2`).
 *
 * Equivalence groups:
 * - 2-pane: 1 preset → stacked full-width → show `2-pane-side-by-side`
 * - 3-pane: each preset has a unique mobile layout → all 3 are distinct
 * - 4-pane: both presets → identical 2×2 grid → show `4-pane-grid`
 * - 5-pane: grouped by mobileSpanningSlot value ('1', '3', '5') → 3 representatives
 * - 6-pane: all 6 presets → identical 2×3 grid → show `6-pane-3x2-rows-first`
 */
export const MOBILE_DISTINCT_PRESETS: readonly LayoutPresetId[] = [
  '2-pane-side-by-side',
  '3-pane-three-across',
  '3-pane-1-left-2-right',
  '3-pane-2-left-1-right',
  '4-pane-grid',
  '5-pane-band-top',       // mobileSpanningSlot: '1' (full-width top row)
  '5-pane-band-middle',    // mobileSpanningSlot: '3' (full-width middle row)
  '5-pane-band-bottom',    // mobileSpanningSlot: '5' (full-width bottom row)
  '6-pane-3x2-rows-first',
];

/**
 * Maps any preset to its mobile-visual representative for active indicator display.
 *
 * On mobile, multiple presets render identically (same CSS grid layout).
 * This function finds the representative preset from `MOBILE_DISTINCT_PRESETS`
 * that matches the given preset by `family`, `mobileSpanningSlot`, and `mobileLayout`.
 *
 * Returns the original `presetId` if no match is found (safe fallback).
 */
export function getMobileEquivalentPresetId(presetId: LayoutPresetId): LayoutPresetId {
  const preset = LAYOUT_PRESETS[presetId];
  const match = MOBILE_DISTINCT_PRESETS.find((id) => {
    const candidate = LAYOUT_PRESETS[id];
    return candidate.family === preset.family
      && candidate.mobileSpanningSlot === preset.mobileSpanningSlot
      && candidate.mobileLayout === preset.mobileLayout;
  });
  return match ?? presetId;
}

// ===== PANE CONSTRAINT CONSTANTS =====

/** Absolute pixel floor for pane minimum size — prevents collapse on small containers */
export const MIN_PANE_PX_FLOOR = 80;

/** Minimum pane ratio (fraction of split-axis dimension) — 10% */
export const MIN_PANE_RATIO = 0.1;

/** Maximum pane size as percentage string — passed directly to SplitPane maxSize prop */
export const MAX_PANE_PERCENT = '90%';

// ===== COMPILE-TIME COMPLETENESS CHECKS =====

type _AllPresetIdsCovered = Exclude<LayoutPresetId, (typeof LAYOUT_PRESET_IDS)[number]> extends never ? true : never;
type _AllSlotIdsCovered = Exclude<SlotId, (typeof SLOT_IDS)[number]> extends never ? true : never;

/**
 * @internal
 * Compile-time coverage checks only — bundled export to eliminate unused variable warnings
 * while maintaining drift protection. Do NOT re-export from barrels.
 */
export type _LayoutTypeChecks = [
  _AllPresetIdsCovered,
  _AllSlotIdsCovered,
];
